/**
 * vmx-cart-drawer.js
 * Defines the <vmx-cart-drawer> Web Component.
 *
 * Security: NEVER uses innerHTML for any user-controlled content.
 * All text via textContent; markup via cloneNode(true) from <template> elements.
 *
 * Events consumed:
 *   vmx:cart:opened  — open drawer + fetch cart
 *   vmx:cart:updated — re-fetch + re-render
 *
 * Events dispatched:
 *   vmx:cart:closed  — fired when drawer closes
 *
 * Globals read:
 *   window.VMX_FREE_DELIVERY_THRESHOLD  (pence; brand default 3000 = £30)
 *   window.VMX_FREE_GIFT_THRESHOLD      (pence; brand default 3999 = £39.99)
 *   window.VMX_GIFT_VARIANT_ID          (string variant id, may be blank)
 *   window.Shopify.formatMoney          (optional, Shopify helper)
 */

(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ── Money formatter ────────────────────────────────────────────── */
  function formatMoney(cents) {
    // Honour the active presentment currency (Shopify Markets) so the drawer
    // matches the rest of the storefront — including the recommendations, which
    // render via Liquid `money`. Hardcoding £ here made the cart show £ while
    // recs showed kr/€ in non-GBP sessions (the "broken" mismatch).
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: cur });
    } catch (e) {
      return (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    }
  }

  /* ── Saving-pill builder ────────────────────────────────────────────
     One reusable tag-pill atom (Grüns-style). `mod` sets the visual variant
     ('sub' | 'save' | 'gift' | 'offer'). Icon is the discount-tag glyph. */
  function pill(label, mod) {
    var el = document.createElement('span');
    el.className = 'vmx-pill' + (mod ? ' vmx-pill--' + mod : '');
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('class', 'vmx-pill__icon');
    svg.setAttribute('aria-hidden', 'true');
    var p = document.createElementNS(ns, 'path');
    p.setAttribute('fill-rule', 'evenodd');
    p.setAttribute('d', 'M8.575 4.649a3.75 3.75 0 0 1 2.7-1.149h1.975a3.25 3.25 0 0 1 3.25 3.25v2.187a3.25 3.25 0 0 1-.996 2.34l-4.747 4.572a2.5 2.5 0 0 1-3.502-.033l-2.898-2.898a2.75 2.75 0 0 1-.036-3.852l4.254-4.417Zm4.425 3.351a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z');
    p.setAttribute('fill', 'currentColor');
    svg.appendChild(p);
    var t = document.createElement('span');
    t.className = 'vmx-pill__text';
    t.textContent = label;
    el.appendChild(svg);
    el.appendChild(t);
    return el;
  }

  /* ── RRP / compare-at helper ────────────────────────────────────────
     cart.js never exposes compare_at_price, so the line's "was" RRP comes
     from window.VMX_COMPARE_AT (variant id → compare-at pence, injected in
     theme.liquid). Falls back to Shopify's original_line_price (covers any
     genuine line-level discount), and takes whichever RRP is higher. ───── */
  function lineRrp(item) {
    var map = window.VMX_COMPARE_AT || {};
    var compareUnit = map[item.variant_id] || 0;
    // The struck "was" is the ACTUAL RRP (compare-at price) whenever one is
    // mapped for this variant — that is the genuine, higher retail price the
    // discount is measured against. Only fall back to Shopify's
    // original_line_price when no RRP is configured for the variant.
    if (compareUnit > 0) return compareUnit * (item.quantity || 1);
    return item.original_line_price || 0;
  }

  /* ── Cart Ajax API helpers ──────────────────────────────────────── */
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function cartChange(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }

  function cartAdd(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }

  /* ── Gift threshold logic — tiered: 1 gift per full threshold ──── */
  function maybeManageGift(cart) {
    var threshold = window.VMX_FREE_GIFT_THRESHOLD;
    var giftId = (window.VMX_GIFT_VARIANT_ID || '').trim();

    if (!threshold) return Promise.resolve(cart);
    if (!giftId) {
      // No theme gift variant configured — any free gift is discount-app-driven.
      // Silent (was a console.warn that fired on every cart open/update).
      return Promise.resolve(cart);
    }

    var giftLineIndex = -1;
    var currentGiftQty = 0;
    var nonGiftSubtotal = 0;

    cart.items.forEach(function (item, idx) {
      var isGift = item.properties && item.properties._is_gift === 'true';
      if (isGift) {
        giftLineIndex = idx + 1; // Shopify lines are 1-indexed
        currentGiftQty = item.quantity;
      } else {
        nonGiftSubtotal += item.final_line_price;
      }
    });

    // One free gift per full threshold tier (buy £40 → 1 free, £80 → 2 free…).
    var desiredGiftQty = Math.max(0, Math.floor(nonGiftSubtotal / threshold));
    if (desiredGiftQty === currentGiftQty) return Promise.resolve(cart);

    if (giftLineIndex === -1) {
      // No gift line yet → add desired quantity in one POST.
      return cartAdd({
        items: [{ id: giftId, quantity: desiredGiftQty, properties: { _is_gift: 'true' } }],
      }).then(fetchCart);
    }

    // Gift line exists → bump or drop to the desired qty (0 removes it).
    return cartChange({ line: giftLineIndex, quantity: desiredGiftQty }).then(fetchCart);
  }

  /* ── Web Component ──────────────────────────────────────────────── */
  class VmxCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._nodes = null;
      this._previousFocus = null;
      this._handleKeyDown = this._handleKeyDown.bind(this);
      this._handleCartOpen = this._handleCartOpen.bind(this);
      this._handleCartUpdated = this._handleCartUpdated.bind(this);
    }

    connectedCallback() {
      document.addEventListener('vmx:cart:opened', this._handleCartOpen);
      document.addEventListener('vmx:cart:updated', this._handleCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('vmx:cart:opened', this._handleCartOpen);
      document.removeEventListener('vmx:cart:updated', this._handleCartUpdated);
      this._unbindKeyDown();
    }

    /* ── Event handlers ───────────────────────────────────────────── */
    _handleCartOpen() {
      if (this._open) return;
      this._mount();
      fetchCart()
        .then(function (cart) { return maybeManageGift(cart); })
        .then(this.render.bind(this))
        .then(this._openDrawer.bind(this))
        .catch(function (err) {
          // Never leave the page in a half-open state: log and bail cleanly so
          // the body stays scrollable and a retry (re-click) can work.
          console.error('[vmx-cart] Failed to open cart:', err);
        });
    }

    _handleCartUpdated() {
      if (!this._nodes) return;
      fetchCart()
        .then(function (cart) { return maybeManageGift(cart); })
        .then(this.render.bind(this))
        .catch(function (err) { console.error('[vmx-cart] Failed to refresh cart:', err); });
    }

    /* ── Mount: clone drawer template ────────────────────────────── */
    _mount() {
      if (this._nodes) return;

      var tmpl = document.getElementById('vmx-cart-drawer-tmpl');
      if (!tmpl) {
        console.error('[vmx-cart] Template #vmx-cart-drawer-tmpl not found.');
        return;
      }

      var fragment = tmpl.content.cloneNode(true);
      this.appendChild(fragment);

      this._nodes = {
        backdrop:        this.querySelector('[data-vmx-cart-backdrop]'),
        panel:           this.querySelector('.vmx-cart-drawer'),
        closeButtons:    this.querySelectorAll('[data-vmx-cart-close]'),
        count:           this.querySelector('[data-vmx-cart-count]'),
        deliveryBanner:  this.querySelector('[data-vmx-cart-delivery]'),
        deliveryMsg:     this.querySelector('[data-vmx-cart-delivery-msg]'),
        deliveryFill:    this.querySelector('[data-vmx-cart-delivery-fill]'),
        linesSlot:       this.querySelector('[data-vmx-cart-lines]'),
        continueLink:    this.querySelector('[data-vmx-cart-continue]'),
        recs:            this.querySelector('[data-vmx-cart-recs]'),
        recsList:        this.querySelector('[data-vmx-cart-recs-list]'),
        footer:          this.querySelector('[data-vmx-cart-footer]'),
        subtotalLabel:   this.querySelector('[data-vmx-cart-subtotal-label]'),
        subtotal:        this.querySelector('[data-vmx-cart-subtotal]'),
        subtotalWas:     this.querySelector('[data-vmx-cart-subtotal-was]'),
        discountRow:     this.querySelector('[data-vmx-cart-discount-row]'),
        discount:        this.querySelector('[data-vmx-cart-discount]'),
        summaryPills:    this.querySelector('[data-vmx-cart-summary-pills]'),
        giftRow:         this.querySelector('[data-vmx-cart-giftrow]'),
        giftVal:         this.querySelector('[data-vmx-cart-giftval]'),
        badge:           this.querySelector('[data-vmx-cart-badge]'),
        total:           this.querySelector('[data-vmx-cart-total]'),
        checkoutLink:    this.querySelector('[data-vmx-cart-checkout]'),
        checkoutLabel:   this.querySelector('[data-vmx-cart-checkout-label]'),
      };

      this._bindClose();
    }

    /* ── Render cart data ─────────────────────────────────────────── */
    render(cart) {
      if (!this._nodes) return;
      var n = this._nodes;

      var totalQty = cart.item_count || 0;
      var items = cart.items || [];

      var nonGiftSubtotal = 0;
      items.forEach(function (item) {
        var isGift = item.properties && item.properties._is_gift === 'true';
        if (!isGift) nonGiftSubtotal += item.final_line_price;
      });

      // Badge count
      if (n.count) {
        n.count.textContent = totalQty;
        n.count.hidden = totalQty === 0;
      }

      // Free-delivery celebration (positive reinforcement only, no upsell nudge).
      this._renderDelivery(nonGiftSubtotal);

      // Lines
      n.linesSlot.replaceChildren();
      if (items.length === 0) {
        var emptyTmpl = document.getElementById('vmx-cart-empty-tmpl');
        if (emptyTmpl) {
          n.linesSlot.appendChild(emptyTmpl.content.cloneNode(true));
        }
        if (n.footer) n.footer.hidden = true;
        if (n.continueLink) n.continueLink.hidden = true;
        if (n.recs) n.recs.hidden = true;
        this._recsAnchorId = null;
        return;
      }

      if (n.continueLink) n.continueLink.hidden = false;
      this._renderRecs(items);

      items.forEach(function (item, idx) {
        n.linesSlot.appendChild(this._buildLineNode(item, idx + 1));
      }, this);

      // Footer
      this._renderFooter(items, nonGiftSubtotal, totalQty, cart.total_price);
    }

    /* ── Free-delivery celebration ─────────────────────────────────────
       Grüns-style positive reinforcement: confirms a reward already earned,
       never a "spend £X more" nudge. Free UK delivery clears at a low
       threshold (brand default £15) so essentially every cart qualifies —
       shown as a won ✓, hidden on the rare sub-threshold cart to avoid any
       upsell framing. The drawer pushes toward checkout, not toward spend. */
    _renderDelivery(nonGiftSubtotal) {
      var n = this._nodes;
      if (!n.deliveryBanner) return;

      var threshold = Number(window.VMX_FREE_DELIVERY_THRESHOLD) || 0;
      var qualified = threshold > 0 ? nonGiftSubtotal >= threshold : nonGiftSubtotal > 0;

      if (qualified) {
        if (n.deliveryMsg) {
          var strong = document.createElement('strong');
          strong.textContent = 'FREE DELIVERY';
          n.deliveryMsg.replaceChildren(document.createTextNode('Congrats! You’ve got '), strong);
        }
        if (n.deliveryFill) n.deliveryFill.style.width = '100%';
        n.deliveryBanner.hidden = false;
      } else {
        n.deliveryBanner.hidden = true;
      }
    }

    /* ── Render "Customers also bought" ───────────────────────────────
       Fetches related products for the first non-gift item via Shopify's
       Product Recommendations API (server-rendered vmx-cart-recommendations
       section), filters out anything already in the basket, then injects the
       rows. Cached per anchor product so cart edits don't refetch. ───────── */
    _renderRecs(items) {
      var n = this._nodes;
      if (!n.recs || !n.recsList) return;

      var anchor = items.find(function (item) {
        return !(item.properties && item.properties._is_gift === 'true');
      });
      if (!anchor || !anchor.product_id) {
        n.recs.hidden = true;
        this._recsAnchorId = null;
        this._recsHtml = null;
        return;
      }

      var self = this;

      // Paint from the cached section HTML, filtering out whatever is in the
      // basket RIGHT NOW. Runs on every render so a just-added item leaves the
      // strip and the cart lines stay in sync — no refetch needed.
      var paint = function () {
        if (!self._recsHtml) { n.recs.hidden = true; return; }
        var inCart = {};
        items.forEach(function (item) { if (item.handle) inCart[item.handle] = true; });

        var doc = new DOMParser().parseFromString(self._recsHtml, 'text/html');
        var rows = Array.prototype.slice.call(doc.querySelectorAll('.vmx-xsell__row'));
        var list = document.createElement('ul');
        list.className = 'vmx-xsell__list';
        list.setAttribute('role', 'list');
        var shown = 0;
        rows.forEach(function (row) {
          if (shown >= 10) return;
          if (inCart[row.getAttribute('data-xsell-handle')]) return; // already in basket
          list.appendChild(document.importNode(row, true));
          shown += 1;
        });
        if (!shown) { n.recs.hidden = true; return; }
        n.recsList.replaceChildren(list);
        n.recs.hidden = false;
        // Let the global review-counts script paint stars on the new rows.
        document.dispatchEvent(new CustomEvent('vmx:reviews:repaint'));
      };

      // Cache hit: same anchor product → re-paint from cached HTML (cheap).
      if (this._recsAnchorId === anchor.product_id && this._recsHtml != null) {
        paint();
        return;
      }

      // Cache miss: fetch related products for the anchor via the Shopify
      // Product Recommendations API (server-rendered vmx-cart-recommendations).
      this._recsAnchorId = anchor.product_id;
      this._recsHtml = null;
      var anchorId = anchor.product_id;
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var url = root + 'recommendations/products'
        + '?section_id=vmx-cart-recommendations'
        + '&intent=related&limit=10'
        + '&product_id=' + encodeURIComponent(anchorId);

      fetch(url, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (self._recsAnchorId !== anchorId) return; // cart changed mid-flight
          self._recsHtml = html || '';
          paint();
        })
        .catch(function () { n.recs.hidden = true; });
    }

    /* ── Build one line node ──────────────────────────────────────── */
    _buildLineNode(item, lineIndex) {
      var tmpl = document.getElementById('vmx-cart-line-tmpl');
      if (!tmpl) return document.createTextNode('');

      var node = tmpl.content.cloneNode(true);
      var isGift = item.properties && item.properties._is_gift === 'true';

      // Image
      var img = node.querySelector('.vmx-cart-line__image');
      if (img) {
        if (item.image) {
          img.setAttribute('src', item.image);
          img.setAttribute('alt', item.title || '');
        } else {
          img.hidden = true;
        }
      }

      // Gift badge
      var giftBadge = node.querySelector('.vmx-cart-line__gift-badge');
      if (giftBadge) giftBadge.hidden = !isGift;

      // Title
      var title = node.querySelector('[data-vmx-line-title]');
      if (title) title.textContent = item.product_title || item.title || '';

      // Meta
      var meta = node.querySelector('[data-vmx-line-meta]');
      if (meta) {
        if (isGift) {
          meta.textContent = 'Free gift · auto-applied';
        } else {
          var variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
          var purchaseType = item.selling_plan_allocation ? 'Subscribe & Save' : 'One-time';
          meta.textContent = [variantTitle, purchaseType].filter(Boolean).join(' · ');
        }
      }

      // Per-day cost — every pack is 45 servings (2/day). Reframes the price as a
      // daily ritual (~44p/day at £19.99). Computed from the ACTUAL unit price so
      // a subscription-discounted line shows its real per-day figure. Skipped on
      // gift/free lines. In non-GBP (Shopify Markets) sessions, show the localised
      // currency rather than a hardcoded "p".
      var perDayEl = node.querySelector('[data-vmx-line-perday]');
      if (perDayEl) {
        var unitPence = item.quantity ? Math.round(item.final_line_price / item.quantity) : 0;
        if (!isGift && unitPence > 0) {
          var perDay = unitPence / 45;
          var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
          var perDayStr = (cur === 'GBP' && perDay < 100) ? Math.round(perDay) + 'p' : formatMoney(perDay);
          perDayEl.textContent = '45-day supply · ~' + perDayStr + '/day';
          perDayEl.hidden = false;
        } else {
          perDayEl.hidden = true;
        }
      }

      // Remove button
      var removeBtn = node.querySelector('[data-vmx-line-remove]');
      if (removeBtn) {
        if (isGift) {
          removeBtn.hidden = true;
        } else {
          removeBtn.addEventListener('click', function () {
            if (removeBtn.disabled) return;
            removeBtn.disabled = true;
            cartChange({ line: lineIndex, quantity: 0 })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) {
                console.error('[vmx-cart] Remove failed:', err);
                removeBtn.disabled = false;
              });
          });
        }
      }

      // Stepper
      var stepper = node.querySelector('[data-vmx-line-stepper]');
      var qtySpan = node.querySelector('[data-vmx-line-qty]');
      if (isGift) {
        if (stepper) stepper.hidden = true;
      } else {
        if (qtySpan) qtySpan.textContent = item.quantity;

        var decrementBtn = node.querySelector('[data-vmx-line-decrement]');
        var incrementBtn = node.querySelector('[data-vmx-line-increment]');
        // Disable BOTH steppers during an in-flight change: the captured
        // item.quantity / lineIndex are stale until the next re-render, so a
        // rapid second click would post a wrong qty or hit a shifted line.
        var lockStepper = function () {
          if (decrementBtn) decrementBtn.disabled = true;
          if (incrementBtn) incrementBtn.disabled = true;
        };
        var unlockStepper = function () {
          if (decrementBtn) decrementBtn.disabled = false;
          if (incrementBtn) incrementBtn.disabled = false;
        };
        if (decrementBtn) {
          decrementBtn.addEventListener('click', function () {
            if (decrementBtn.disabled) return;
            lockStepper();
            var newQty = Math.max(0, item.quantity - 1);
            cartChange({ line: lineIndex, quantity: newQty })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) { console.error('[vmx-cart] Update failed:', err); unlockStepper(); });
          });
        }
        if (incrementBtn) {
          incrementBtn.addEventListener('click', function () {
            if (incrementBtn.disabled) return;
            lockStepper();
            cartChange({ line: lineIndex, quantity: item.quantity + 1 })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) { console.error('[vmx-cart] Update failed:', err); unlockStepper(); });
          });
        }
      }

      // A line is "free" when its final price is 0 — whether a property gift or a
      // 100%-off free unit (e.g. Buy 2 Get 1). Show its real worth either way.
      var isFree = item.final_line_price === 0;

      var rrp = lineRrp(item);

      // Gift / free value text — show the real retail worth so the customer sees the win.
      var giftValue = node.querySelector('[data-vmx-line-gift-value]');
      if (giftValue) {
        if (isFree && rrp > 0) {
          giftValue.textContent = 'Yours free — worth ' + formatMoney(rrp);
          giftValue.hidden = false;
        } else if (isGift) {
          giftValue.textContent = 'Yours free';
          giftValue.hidden = false;
        } else {
          giftValue.hidden = true;
        }
      }

      // Price
      var priceEl = node.querySelector('[data-vmx-line-price]');
      if (priceEl) {
        if (isFree) {
          priceEl.textContent = 'FREE';
          priceEl.classList.add('vmx-cart-line__price--free');
        } else {
          priceEl.textContent = formatMoney(item.final_line_price);
          priceEl.classList.remove('vmx-cart-line__price--free');
        }
      }

      // Compare-at (struck RRP) — show whenever the line sells below its RRP.
      var lineSave = rrp - item.final_line_price;
      var compareEl = node.querySelector('[data-vmx-line-compare]');
      if (compareEl) {
        if (lineSave > 0) {
          compareEl.textContent = formatMoney(rrp);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      // Green per-line savings — "Save £X". Skipped on free/gift lines, where the
      // "Yours free — worth £X" line already tells the savings story.
      var saveEl = node.querySelector('[data-vmx-line-save]');
      if (saveEl) {
        if (lineSave > 0 && !isFree) {
          saveEl.textContent = '(Save ' + formatMoney(lineSave) + ')';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      // Saving pills (Grüns 4d/4e) — named, honest offer tags on the line:
      //  · subscription line → "Subscribe & Save" (the price already reflects it)
      //  · a live saving on the line → "You're saving £X"
      //  · non-gift eligible line → the live Buy-2-Get-1 offer reminder (FAMILY3)
      var pillsSlot = node.querySelector('[data-vmx-line-pills]');
      if (pillsSlot) {
        pillsSlot.replaceChildren();
        if (isGift) {
          pillsSlot.appendChild(pill('Free gift', 'gift'));
        } else {
          if (item.selling_plan_allocation) pillsSlot.appendChild(pill('Subscribe & Save', 'sub'));
          // Discounts ACTUALLY applied to this line, read from cart.js (e.g. the
          // "Buy 2, Get 1 Free" app discount). Truthful by construction — these
          // are the exact titles Shopify will honour at checkout.
          var applied = (item.discounts || []).filter(function (d) { return d && d.title; });
          applied.forEach(function (d) { pillsSlot.appendChild(pill(d.title, 'save')); });
          // Offer reminder — only where the Buy-2-Get-1 is NOT yet applied to this
          // line (so a free/entitled line shows its real discount, not a nudge).
          if (window.VMX_B2G1_ENABLED !== false && applied.length === 0 && !isFree) {
            var buyN = Number(window.VMX_B2G1_BUY) || 2;
            var getN = Number(window.VMX_B2G1_GET) || 1;
            pillsSlot.appendChild(pill('Get ' + (buyN + getN) + ', pay ' + buyN, 'offer'));
          }
        }
      }

      return node;
    }

    /* ── Render footer ────────────────────────────────────────────── */
    _renderFooter(items, nonGiftSubtotal, totalQty, totalPrice) {
      var n = this._nodes;
      if (!n.footer) return;
      n.footer.hidden = false;

      // RRP of the purchased (non-gift) items, and the worth of any free gift.
      var originalTotal = items.reduce(function (sum, item) {
        var isGift = item.properties && item.properties._is_gift === 'true';
        return isGift ? sum : sum + lineRrp(item);
      }, 0);
      var giftWorth = items.reduce(function (sum, item) {
        var isGift = item.properties && item.properties._is_gift === 'true';
        return isGift ? sum + lineRrp(item) : sum;
      }, 0);
      // Discount is the markdown on purchased items. Subtotal − Discount = Total,
      // so the receipt always reconciles. The free gift is shown as a bonus row
      // (not subtracted — it never inflated the total to begin with).
      var discount = Math.max(0, originalTotal - nonGiftSubtotal);

      // Subtotal (RRP of purchased items)
      if (n.subtotal) n.subtotal.textContent = formatMoney(originalTotal);

      // Discount row + named saving pills
      if (n.discountRow && n.discount) {
        if (discount > 0) {
          n.discount.textContent = '-' + formatMoney(discount);
          n.discountRow.hidden = false;
          if (n.summaryPills) {
            n.summaryPills.replaceChildren();
            var hasSub = items.some(function (item) {
              return item.selling_plan_allocation && !(item.properties && item.properties._is_gift === 'true');
            });
            n.summaryPills.appendChild(hasSub ? pill('Subscribe & Save', 'sub') : pill('WELCOME26', 'save'));
          }
        } else {
          n.discountRow.hidden = true;
        }
      }

      // Free-gift bonus row (informational, not subtracted from the total)
      if (n.giftRow && n.giftVal) {
        if (giftWorth > 0) {
          n.giftVal.textContent = 'worth ' + formatMoney(giftWorth);
          n.giftRow.hidden = false;
        } else {
          n.giftRow.hidden = true;
        }
      }

      // Total + struck RRP + % badge (badge only when the % is real vs RRP)
      if (n.total) n.total.textContent = formatMoney(nonGiftSubtotal);
      if (n.subtotalWas) {
        if (discount > 0) {
          n.subtotalWas.textContent = formatMoney(originalTotal);
          n.subtotalWas.hidden = false;
        } else {
          n.subtotalWas.hidden = true;
        }
      }
      if (n.badge) {
        var pct = originalTotal > 0 ? Math.round((discount / originalTotal) * 100) : 0;
        if (pct > 0) {
          n.badge.textContent = pct + '% OFF';
          n.badge.hidden = false;
        } else {
          n.badge.hidden = true;
        }
      }

      // Checkout label — "Secure" reassurance cue at the payment-anxiety moment.
      if (n.checkoutLabel) {
        n.checkoutLabel.textContent = 'Secure checkout · ' + formatMoney(nonGiftSubtotal);
      }
    }

    /* ── Bind close events ────────────────────────────────────────── */
    _bindClose() {
      var self = this;
      if (this._nodes.backdrop) {
        this._nodes.backdrop.addEventListener('click', function () { self.close(); });
      }
      this._nodes.closeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
    }

    /* ── Open / close ─────────────────────────────────────────────── */
    _openDrawer() {
      this._open = true;
      this._previousFocus = document.activeElement;
      // Lock BOTH <html> and <body>: on this theme <html> is the scroll
      // container, so locking body alone leaves the page scrolling behind the
      // open drawer. (The hook popup locks documentElement for the same reason.)
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      // Flag the open state so competing fixed CTAs (e.g. the PDP sticky add
      // bar) can hide themselves while the drawer is up.
      document.documentElement.classList.add('vmx-cart-open');
      this._bindKeyDown();
      requestAnimationFrame(function () {
        var first = this.querySelector(FOCUSABLE);
        if (first) first.focus();
      }.bind(this));
    }

    open() {
      document.dispatchEvent(new CustomEvent('vmx:cart:opened'));
    }

    close() {
      if (!this._open) return;
      this._open = false;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.documentElement.classList.remove('vmx-cart-open');
      this._unbindKeyDown();

      var panel = this._nodes && this._nodes.panel;
      var backdrop = this._nodes && this._nodes.backdrop;
      var self = this;

      function cleanup() {
        self._nodes = null;
        while (self.firstChild) self.removeChild(self.firstChild);
        document.dispatchEvent(new CustomEvent('vmx:cart:closed'));
      }

      if (panel) {
        panel.classList.add('is-closing');
        if (backdrop) backdrop.style.opacity = '0';
        setTimeout(cleanup, 320);
      } else {
        cleanup();
      }

      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    }

    /* ── Focus trap / keyboard ────────────────────────────────────── */
    _bindKeyDown() { document.addEventListener('keydown', this._handleKeyDown); }
    _unbindKeyDown() { document.removeEventListener('keydown', this._handleKeyDown); }

    _handleKeyDown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') {
        var focusable = Array.from(this.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    }
  }

  customElements.define('vmx-cart-drawer', VmxCartDrawer);

  /* ── Header badge click → open cart ────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vmx-cart-open], [data-vmx-open-cart]');
    if (btn) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('vmx:cart:opened'));
    }
  });
})();
